import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useCreateTemplate, useUpdateTemplate, useTemplate } from '../../hooks/useTemplates';
import { Loader2 } from 'lucide-react';
import { GrantType, ProviderTypes } from '../../types';
import type { ProviderType } from '../../types';

interface TemplateFormData {
  providerType: ProviderType;
  name: string;
  authorizationUrl: string;
  tokenUrl: string;
  userInfoUrl: string;
  revocationUrl: string;
  defaultScopes: string;
  requiresPkce: boolean;
  tokenEndpointAuthMethod: string;
  isActive: boolean;
}

export const TemplateForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;

  const { data: template, isLoading: isLoadingTemplate } = useTemplate(id || '', !!id);
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate();

  const { register, handleSubmit, formState: { errors }, setValue } = useForm<TemplateFormData>({
    defaultValues: {
      requiresPkce: false,
      isActive: true,
      tokenEndpointAuthMethod: 'client_secret_post',
    }
  });

  // Load template data for editing
  useEffect(() => {
    if (template) {
      setValue('providerType', template.providerType);
      setValue('name', template.name);
      setValue('authorizationUrl', template.authorizationUrl);
      setValue('tokenUrl', template.tokenUrl);
      setValue('userInfoUrl', template.userInfoUrl || '');
      setValue('revocationUrl', template.revocationUrl || '');

      // Handle defaultScopes - backend may return JSON string, parse it to plain string
      let scopesString = typeof template.defaultScopes === 'string' ? template.defaultScopes : '';
      if (typeof scopesString === 'string' && scopesString.startsWith('[')) {
        // It's a JSON array string like '["openid","profile"]', parse and convert to space-separated
        try {
          const parsed = JSON.parse(scopesString);
          scopesString = Array.isArray(parsed) ? parsed.join(' ') : scopesString;
        } catch {
          // If parsing fails, use as-is
        }
      }
      setValue('defaultScopes', scopesString);

      setValue('requiresPkce', template.requiresPkce);
      setValue('tokenEndpointAuthMethod', template.tokenEndpointAuthMethod);
      setValue('isActive', template.isActive);
    }
  }, [template, setValue]);

  const onSubmit = async (data: TemplateFormData) => {
    try {
      // Backend expects DefaultScopes and SupportedGrantTypes as space-separated strings
      const payload = {
        providerType: data.providerType,
        name: data.name,
        authorizationUrl: data.authorizationUrl,
        tokenUrl: data.tokenUrl,
        userInfoUrl: data.userInfoUrl || undefined,
        revocationUrl: data.revocationUrl || undefined,
        defaultScopes: data.defaultScopes.trim(), // Plain string (space-separated)
        requiresPkce: data.requiresPkce,
        supportedGrantTypes: `${GrantType.AuthorizationCode} ${GrantType.RefreshToken}`,
        tokenEndpointAuthMethod: data.tokenEndpointAuthMethod,
        isActive: data.isActive,
      };

      if (isEdit && id) {
        await updateTemplate.mutateAsync({ templateId: id, data: payload });
      } else {
        await createTemplate.mutateAsync(payload);
      }

      navigate('/templates');
    } catch (error) {
      console.error('Failed to save template:', error);
    }
  };

  if (isLoadingTemplate) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Provider Template' : 'Create Provider Template'}
        </h2>
        <p className="text-gray-600 mt-1">
          {isEdit ? 'Update template configuration' : 'Add a new OAuth provider template'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="card p-6 space-y-6">
        {/* Provider Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Provider Type *</label>
          <select
            {...register('providerType', { required: true })}
            className="input w-full"
            disabled={isEdit}
            defaultValue=""
          >
            <option value="" disabled>Select provider type</option>
            {ProviderTypes.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Must match one of the supported provider types (enum on backend).
          </p>
          {errors.providerType && <p className="text-sm text-red-600 mt-1">Provider type is required</p>}
        </div>

        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
          <input
            {...register('name', { required: true })}
            type="text"
            className="input w-full"
            placeholder="Google OAuth 2.0"
          />
          {errors.name && <p className="text-sm text-red-600 mt-1">Name is required</p>}
        </div>

        {/* Authorization URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Authorization URL *</label>
          <input
            {...register('authorizationUrl', { required: true })}
            type="url"
            className="input w-full"
            placeholder="https://accounts.google.com/o/oauth2/v2/auth"
          />
          {errors.authorizationUrl && <p className="text-sm text-red-600 mt-1">Authorization URL is required</p>}
        </div>

        {/* Token URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Token URL *</label>
          <input
            {...register('tokenUrl', { required: true })}
            type="url"
            className="input w-full"
            placeholder="https://oauth2.googleapis.com/token"
          />
          {errors.tokenUrl && <p className="text-sm text-red-600 mt-1">Token URL is required</p>}
        </div>

        {/* User Info URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">User Info URL</label>
          <input
            {...register('userInfoUrl')}
            type="url"
            className="input w-full"
            placeholder="https://www.googleapis.com/oauth2/v2/userinfo"
          />
        </div>

        {/* Revocation URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Revocation URL</label>
          <input
            {...register('revocationUrl')}
            type="url"
            className="input w-full"
            placeholder="https://oauth2.googleapis.com/revoke"
          />
        </div>

        {/* Default Scopes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Default Scopes *</label>
          <input
            {...register('defaultScopes', { required: true })}
            type="text"
            className="input w-full"
            placeholder="openid profile email"
          />
          <p className="text-sm text-gray-500 mt-1">Space-separated list of scopes</p>
          {errors.defaultScopes && <p className="text-sm text-red-600 mt-1">At least one scope is required</p>}
        </div>

        {/* Token Endpoint Auth Method */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Token Endpoint Auth Method *</label>
          <select
            {...register('tokenEndpointAuthMethod', { required: true })}
            className="input w-full"
          >
            <option value="client_secret_post">client_secret_post</option>
            <option value="client_secret_basic">client_secret_basic</option>
            <option value="none">none</option>
          </select>
        </div>

        {/* Checkboxes */}
        <div className="space-y-3">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              {...register('requiresPkce')}
              className="rounded"
            />
            <span className="text-sm font-medium">Require PKCE</span>
          </label>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              {...register('isActive')}
              className="rounded"
            />
            <span className="text-sm font-medium">Active</span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex space-x-4 pt-4">
          <button
            type="submit"
            disabled={createTemplate.isPending || updateTemplate.isPending}
            className="btn btn-primary px-6 py-2 flex items-center space-x-2"
          >
            {(createTemplate.isPending || updateTemplate.isPending) && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            <span>
              {(createTemplate.isPending || updateTemplate.isPending)
                ? 'Saving...'
                : isEdit
                ? 'Update Template'
                : 'Create Template'}
            </span>
          </button>
          <button
            type="button"
            onClick={() => navigate('/templates')}
            className="btn btn-outline px-6 py-2"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};
